import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '../../context';
import { WelcomeBannerSkeleton } from '../home/Skeleton';
import FeaturedMatchSlide from '../home/FeaturedMatchSlide';

const DEFAULT_GLOW = 'rgba(18,20,28,0.5)';
const glowColorCache = new Map();

function slideImageUrl(item) {
  if (!item) return null;
  if (item.kind === 'match') return item.match?.banner_image || null;
  return item.mobileImg || item.img || null;
}

/** Dominant saturated color from banner art (CORS-safe when server sends ACAO). */
export function sampleBannerColor(rawUrl) {
  if (!rawUrl) return Promise.resolve(null);
  let url = rawUrl;
  try {
    url = url.replace(/^https?:\/\/[^/]+\/(uploads|game-icons)\//, '/$1/');
  } catch {
    /* ignore */
  }
  if (glowColorCache.has(url)) return Promise.resolve(glowColorCache.get(url));

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = 24;
        const h = 12;
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h).data;
        let r = 0;
        let g = 0;
        let b = 0;
        let n = 0;
        for (let i = 0; i < d.length; i += 4) {
          const pr = d[i];
          const pg = d[i + 1];
          const pb = d[i + 2];
          const pa = d[i + 3] / 255;
          if (pa < 0.1) continue;
          const mx = Math.max(pr, pg, pb);
          const mn = Math.min(pr, pg, pb);
          const sat = mx > 0 ? (mx - mn) / mx : 0;
          const wt = sat * sat * (mx / 255) * pa;
          r += pr * wt;
          g += pg * wt;
          b += pb * wt;
          n += wt;
        }
        if (!n) {
          glowColorCache.set(url, null);
          return resolve(null);
        }
        r = Math.round(r / n);
        g = Math.round(g / n);
        b = Math.round(b / n);
        const mx = Math.max(r, g, b, 1);
        const target = 185;
        if (mx < target) {
          const s = target / mx;
          r = Math.min(255, Math.round(r * s));
          g = Math.min(255, Math.round(g * s));
          b = Math.min(255, Math.round(b * s));
        }
        const col = `rgb(${r}, ${g}, ${b})`;
        glowColorCache.set(url, col);
        resolve(col);
      } catch {
        glowColorCache.set(url, null);
        resolve(null);
      }
    };
    img.onerror = () => {
      glowColorCache.set(url, null);
      resolve(null);
    };
    img.src = url;
  });
}

export default function WelcomeBannerMobile({
  slides,
  loading,
  onSlideClick,
  onSportEvent,
}) {
  const { locale } = useLocale();
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [glowColor, setGlowColor] = useState(DEFAULT_GLOW);
  const viewportRef = useRef(null);
  const scrollingRef = useRef(false);
  const total = slides.length;
  const slidesKey = useMemo(
    () => slides.map((item) => (
      item.kind === 'match'
        ? `m:${item.match?.id}`
        : `p:${item.img}|${item.showTime}`
    )).join(';;'),
    [slides],
  );

  const go = useCallback((next, { smooth = true } = {}) => {
    if (!total) return;
    const idx = ((next % total) + total) % total;
    const el = viewportRef.current;
    const from = slide;
    const isWrapForward = from === total - 1 && idx === 0;
    setSlide(idx);
    if (!el || !el.clientWidth) return;

    scrollingRef.current = true;
    el.scrollTo({
      left: idx * el.clientWidth,
      behavior: smooth && !isWrapForward ? 'smooth' : 'auto',
    });
    window.setTimeout(() => {
      scrollingRef.current = false;
    }, smooth && !isWrapForward ? 450 : 0);
  }, [slide, total]);

  useEffect(() => {
    if (paused || !total) return undefined;
    const ms = (slides[slide]?.showTime || 5) * 1000;
    const t = window.setInterval(() => go(slide + 1, { smooth: true }), ms);
    return () => clearInterval(t);
  }, [slide, total, go, paused, slides]);

  useEffect(() => {
    setSlide(0);
    const el = viewportRef.current;
    if (el) el.scrollTo({ left: 0, behavior: 'auto' });
  }, [slidesKey]);

  // Glow follows THIS slider's active slide (not desktop parent's index).
  useEffect(() => {
    const url = slideImageUrl(slides[slide]);
    if (!url) {
      setGlowColor(DEFAULT_GLOW);
      return undefined;
    }
    let alive = true;
    sampleBannerColor(url).then((col) => {
      if (!alive) return;
      setGlowColor(col || DEFAULT_GLOW);
    });
    return () => { alive = false; };
  }, [slide, slides]);

  const onScroll = () => {
    if (scrollingRef.current) return;
    const el = viewportRef.current;
    if (!el || !el.clientWidth) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== slide && idx >= 0 && idx < total) setSlide(idx);
  };

  if (!slides.length) {
    return loading ? <WelcomeBannerSkeleton /> : null;
  }

  const duration = slides[slide]?.showTime || 5;

  return (
    <section
      className="welcome-slider-mobile welcome-slider-mobile--photo welcome-slider-mobile--glow"
      style={{ '--banner-glow': glowColor }}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <div className="welcome-banner__glow welcome-slider-mobile__glow" aria-hidden="true" />
      <div className="welcome-slider-mobile__frame">
        <div
          ref={viewportRef}
          className="welcome-slider-mobile__viewport"
          onScroll={onScroll}
        >
          <div className="welcome-slider-mobile__track">
            {slides.map((item, i) => {
              if (item.kind === 'match') {
                return (
                  <article key={`match-${item.match?.id || i}`} className="welcome-slider-mobile__slide welcome-slider-mobile__slide--match">
                    <FeaturedMatchSlide
                      match={item.match}
                      locale={locale}
                      onOpen={onSportEvent}
                      onOddOpen={onSportEvent}
                    />
                  </article>
                );
              }

              const near = i === slide || i === (slide + 1) % Math.max(total, 1);
              const src = item.mobileImg || item.img;
              const clickable = Boolean(
                item.promoId
                || item.href
                || (item.ctaAction && !['none', 'url', ''].includes(item.ctaAction)),
              );
              return (
                <article key={`promo-${item.img}-${i}`} className="welcome-slider-mobile__slide">
                  <button
                    type="button"
                    className="welcome-slider-mobile__card"
                    onClick={() => onSlideClick?.(item)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: 0,
                      border: 0,
                      background: 'none',
                      cursor: clickable ? 'pointer' : 'default',
                    }}
                  >
                    {near ? (
                      <img
                        className="welcome-slider-mobile__img"
                        src={src}
                        alt={item.title?.replace(/<[^>]+>/g, '') || 'Promo banner'}
                        decoding="async"
                        fetchPriority={i === slide ? 'high' : 'low'}
                        loading={i === slide ? 'eager' : 'lazy'}
                        draggable={false}
                      />
                    ) : null}
                  </button>
                </article>
              );
            })}
          </div>
        </div>
      </div>
      <div className="welcome-slider-mobile__indicators" aria-hidden={total <= 1}>
        {slides.map((_, i) => (
          <button
            key={i === slide ? `active-${slide}` : i}
            type="button"
            className={`welcome-slider-mobile__dot${i === slide ? ' is-active' : ''}`}
            style={i === slide ? { '--dot-duration': `${duration}s` } : undefined}
            onClick={() => go(i)}
            aria-label={`Slide ${i + 1}`}
            aria-current={i === slide ? 'true' : undefined}
          />
        ))}
      </div>
    </section>
  );
}
